import { BulkUpdateFieldType, BulkUpdateSaleLotModalProps } from "components/BulkUpdateOptionalFieldsModal/types";
import React from "react";
import { BulkUpdateOptionalFieldsModal } from "components/BulkUpdateOptionalFieldsModal/Modal";
import { bulkUpdateSaleLotsActionCreator } from "actions";
import {
  baseLivestockFields,
  buyerFields,
  cattleAdvancedDraftingFields,
  commonAdvancedDraftingFields,
  dentitionFields,
  EIDExemptionFields,
  estimatedLiveWeightFields,
  sheepAdvancedDraftingFields,
  sheepDraftingAttributes,
  sheepJoiningFields,
  weightRangeAndDressingFields,
  transporterFields,
} from "components/BulkUpdateOptionalFieldsModal/SaleLots/Fields";
import { validationSchema } from "components/BulkUpdateOptionalFieldsModal/SaleLots/ValidationSchema";
import { Species } from "constants/species";
import { useSelector } from "react-redux";
import {
  getContextByModalType,
  getCurrentSale,
  getSaleLots,
  getSetting,
  getSaleWriteAccessDeniedReason,
  SaleLockReason,
  selectAreWeightAndDressingFeaturesEnabled,
  selectDeploymentIdBySaleLotIdLookup,
  selectIsSaleSubTypeDentitionEnabled,
} from "selectors";
import {
  useHasDentitionPermission,
  useHasSaleyardOrDeploymentPermission,
  useHasSaleyardPermission,
} from "hooks";
import {
  DeploymentPermission,
  SaleyardPermission,
} from "constants/permissions";
import { ModalTypes } from "constants/navigation";
import { ModelNames } from "components/BulkUpdateOptionalFieldsModal/util";

// "Configures" relevant fields for update - this can/will/should be extended to
// - include different fields for different sale (sub) types.
// - hoist (rootFieldOrder) some fields for some users.
// - permission hide/show relevent fields.
function useGetBulkUpdateFields() {
  // Drive what's shown based on the sale species.
  const sale = useSelector(getCurrentSale);
  const isSheepSale = sale.species_id === Species.SHEEP;
  const isWeightRangeAndDressingEnabled = useSelector(
    selectAreWeightAndDressingFeaturesEnabled,
  );

  const isDentitionEnabled = useSelector(selectIsSaleSubTypeDentitionEnabled);

  const hasDentitionPermission = useHasDentitionPermission();

  const hasWeightRangePermission = useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureWeightRange,
    SaleyardPermission.featureWeightRange,
  );

  const showWeightRangeAndDressing =
    isWeightRangeAndDressingEnabled && hasWeightRangePermission;

  const showDentition = isDentitionEnabled && hasDentitionPermission;

  const hasTransporterPermission = useHasSaleyardPermission(
    SaleyardPermission.featureTransporters,
  );

  const saleWriteAccessDeniedReason = useSelector(getSaleWriteAccessDeniedReason);

  const rawFields = isSheepSale
    ? baseLivestockFields
        .concat(showWeightRangeAndDressing ? weightRangeAndDressingFields : [])
        .concat(showDentition ? dentitionFields : [])
        .concat(estimatedLiveWeightFields)
        .concat(sheepJoiningFields)
        .concat(EIDExemptionFields)
        .concat(sheepAdvancedDraftingFields)
        .concat(sheepDraftingAttributes)
        .concat(commonAdvancedDraftingFields)
        .concat(hasTransporterPermission ? transporterFields : [])
        .concat(buyerFields)
    : baseLivestockFields
        .concat(showWeightRangeAndDressing ? weightRangeAndDressingFields : [])
        .concat(showDentition ? dentitionFields : [])
        .concat(estimatedLiveWeightFields)
        .concat(cattleAdvancedDraftingFields)
        .concat(commonAdvancedDraftingFields)
        .concat(hasTransporterPermission ? transporterFields : [])
        .concat(buyerFields);

  const dentitionFieldNames = new Set(
    dentitionFields.map((f: BulkUpdateFieldType) => f.fieldName),
  );

  return rawFields.map((field: BulkUpdateFieldType) => {
    const isDentition = dentitionFieldNames.has(field.fieldName);
    const allowedReasons = isDentition
      ? [SaleLockReason.NONE, SaleLockReason.SALE_LOCKED]
      : [SaleLockReason.NONE];
    const disabled = !allowedReasons.includes(saleWriteAccessDeniedReason);
    return disabled ? { ...field, disabled: true } : field;
  });
}

export function BulkUpdateSaleLotsModal(
  props: BulkUpdateSaleLotModalProps,
): React.JSX.Element {
  const { onClose } = props;

  const { saleLotIds, storedSaleLotIdsSetting, aggridRef } =
    useSelector(getContextByModalType(ModalTypes.BulkUpdateSaleLot)) || {};

  const storedSaleLotIds =
    useSelector(getSetting(storedSaleLotIdsSetting)) || null;

  const modelIds = saleLotIds || storedSaleLotIds;
  const bulkUpdateFields = useGetBulkUpdateFields();

  const saleLots = useSelector(getSaleLots);
  const deploymentSaleIdLookup = useSelector(
    selectDeploymentIdBySaleLotIdLookup,
  );

  const deploymentSaleIds = new Set<number>();

  modelIds?.forEach(modelId => {
    if (saleLots[modelId]) {
      deploymentSaleIds.add(deploymentSaleIdLookup[modelId]);
    }
  });

  const productField = bulkUpdateFields.find(f => f.fieldName === "product_id");
  if (productField) {
    const isLockedByAccess = productField.disabled === true && !productField.helpText?.startsWith("Product bulk update");
    if (deploymentSaleIds.size > 1) {
      productField.disabled = true;
      productField.helpText =
        "Product bulk update not allowed for multiple agencies";
    } else {
      productField.disabled = isLockedByAccess;
      productField.helpText = "";
    }
  }
  const deploymentId =
    deploymentSaleIds.size === 1
      ? deploymentSaleIds.values().next().value
      : null;

  return (
    <BulkUpdateOptionalFieldsModal
      onClose={onClose}
      modelIds={modelIds}
      modelName={ModelNames.SALE_LOT}
      bulkUpdateActionCreator={bulkUpdateSaleLotsActionCreator}
      fields={bulkUpdateFields}
      initialValues={{}}
      modelObjects={saleLots}
      validationSchema={validationSchema}
      aggridRef={aggridRef}
      deploymentId={deploymentId}
    />
  );
}
